/**
 * **arcade テーマの盤の描き方**（Issue #523）。
 *
 * ここが持つのは**層をどう描くか**だけ。
 *
 * - **層が何であるか**（名前・順・前提・既定の濃さ）は `core/theme` の `FIELD_LAYERS`
 * - **紙の大きさと解像度・投影・`matrix3d` での貼り付け・濃さの扱い**は `stage/FieldLayer.js`（器）
 *
 * ## 平面の座標で描く
 *
 * 2D canvas の変換はアフィンまでで**透視ができない**ので、「舞台のピクセルに遠近付きで描く」はできない。
 * 器が**平面の座標の紙**を用意して `matrix3d` で貼るので、ここは**平面の座標のまま描く**
 * （紙の原点ずれも倍率も知らない）。おかげで**カメラを知らない**。
 *
 * 描く順（`core/theme` の宣言順）: `matBack` → `video` → `matFront` → `lines`。
 * **マットを地と枠線に分けてある**ので、枠線が実写動画の上に乗る（Issue #523 の狙い）。
 *
 * **カードはここには無い**（Issue #523）—— 盤に**置かれているもの**はオブジェクトの層
 * （`drawObjects.js`）の仕事で、ここが描くのは盤に**印刷されているもの**だけ。
 */
import { withDefaults } from '../../core/theme';
import { fitText, glowStroke, inQuad, quadPath, rgba, roundRectPath, scaleQuad } from '../../canvas/paint';
import { decorOf } from '../../core/decor';
import { videoPlaneRect } from '../../core/field';
import { parseZoneKey } from '../../core/field/zones';
import { PLAYER_COLORS } from '../../core/hud';

/**
 * ゾーンの色。**`matFront` と `lines` で同じ表を使う**（枠と区画がずれた色になるのを防ぐ）。
 * 宣言の `part` は**層の名前**（`field:matBack` / `field:matFront`）—— 部品の単位を
 * 「動かせるもの＝層」に揃えてあるので、GUI のフォルダにトランスフォームと一緒に並ぶ（Issue #523）。
 */
/**
 * **触れるもの**（Issue #523）。`drawField` は canvas なので CSS 変数は効かない ——
 * 宣言と保存は HUD と同じ 1 つで、**こちらは `cfg.style` の値をそのまま読む**。
 */
export const FIELD_SCHEMA = Object.freeze({
  matInner: { type: 'color', label: 'マットの中心', part: 'field:matBack' },
  matMid: { type: 'color', label: 'マットの中ほど', part: 'field:matBack' },
  matOuter: { type: 'color', label: 'マットの外', part: 'field:matBack' },
  matBandA: { type: 'number', label: 'プレイヤーの帯の濃さ', min: 0, max: 0.6, step: 0.01, part: 'field:matBack' },
  matGridA: { type: 'number', label: '格子の濃さ', min: 0, max: 0.5, step: 0.01, part: 'field:matBack' },
  matCenterW: { type: 'number', label: '中央線の太さ', min: 0, max: 20, step: 0.5, part: 'field:matBack' },
  matCenterGlow: { type: 'number', label: '中央線の光', min: 0, max: 60, step: 1, part: 'field:matBack' },
  matEdgeA: { type: 'number', label: '外周の濃さ', min: 0, max: 1, step: 0.05, part: 'field:matBack' },
  zoneMonster: { type: 'color', label: 'モンスターの区画', part: 'field:matFront' },
  zoneMagicTrap: { type: 'color', label: '魔法罠の区画', part: 'field:matFront' },
  zoneSingle: { type: 'color', label: 'その他の区画', part: 'field:matFront' },
  zoneFillA: { type: 'number', label: '区画の地の濃さ', min: 0, max: 0.5, step: 0.01, part: 'field:matFront' },
  zoneEdgeA: { type: 'number', label: '区画の縁の濃さ', min: 0, max: 1, step: 0.05, part: 'field:matFront' },
});

/**
 * **飾りを名乗る部品**（Issue #523）。区画の縁は「太さ ＋ にじみ」＝ `core/decor` の語彙そのものなので、
 * 太さとにじみの数値はここに書かず `dc/field:matFront/*` に置く（既定値は `FIELD_STYLE`）。
 */
export const FIELD_PARTS = Object.freeze([
  Object.freeze({ part: 'field:matFront', decor: ['stroke', 'glow'] }),
]);

/** 既定値（**ここが唯一の真実**。絵の中に数字を残さない） */
export const FIELD_STYLE = Object.freeze({
  matInner: '#1a2748', matMid: '#0b1122', matOuter: '#03050b',
  matBandA: 0.13, matGridA: 0.10, matCenterW: 5, matCenterGlow: 18, matEdgeA: 0.35,
  zoneMonster: '#55ccff', zoneMagicTrap: '#cc99ff', zoneSingle: '#ffdd66',
  zoneFillA: 0.08, zoneEdgeA: 0.85,
  // 区画の縁（`core/decor` の組。太い線を 1 枚薄く重ねてから細い線を乗せる）
  'dc/field:matFront/strokeW': 6,
  'dc/field:matFront/glowW': 16,
  'dc/field:matFront/glowA': 0.22,
});

const withField = withDefaults(() => [FIELD_STYLE]);
const fieldStyle = (cfg) => withField(cfg?.style);

const zoneColors = (v) => ({ monster: v.zoneMonster, magic_trap: v.zoneMagicTrap, single: v.zoneSingle });

const zoneColor = (kind, v = FIELD_STYLE) => zoneColors(v)[kind] ?? zoneColors(v).single;

/** 1 枚ぶんの場所に刷る文字（`core/field/zones.js` の `SINGLE_KINDS` と 1 対 1） */
const ZONE_LABELS = Object.freeze({
  field: 'FIELD', maindeck: 'DECK', grave: 'GY', removed: 'BANISH', extra: 'EX', hand: 'HAND',
});

/**
 * マットの地。絵は持たないので、平面そのものを塗る。
 *
 * **プレイヤーの色は「設定」ではなく「値」**なので `scene` から読む（`config` は設定だけ）。
 * 渡されなければ既定の色に落ちる —— **値が無いだけで落ちないこと**を `drawField.test.js` が見る。
 */
export function drawMatBackground(ctx, scene, cfg) {
  const v = fieldStyle(cfg);
  const { w, h } = scene.field.plane;
  const g = ctx.createRadialGradient(w / 2, h / 2, 0, w / 2, h / 2, Math.max(w, h) * 0.75);
  g.addColorStop(0, v.matInner);
  g.addColorStop(0.6, v.matMid);
  g.addColorStop(1, v.matOuter);
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, w, h);

  // プレイヤーの色の帯（**奥が P2・手前が P1**。`field.side()` に従う）
  [0, 1].forEach((player) => {
    const color = scene.players?.[player]?.color ?? PLAYER_COLORS[player];
    const bottom = scene.field.side(player) === 'bottom';
    const band = h * 0.22;
    const from = bottom ? h : 0;
    const to = bottom ? h - band : band;
    const grad = ctx.createLinearGradient(0, from, 0, to);
    grad.addColorStop(0, rgba(color, v.matBandA));
    grad.addColorStop(1, rgba(color, 0));
    ctx.fillStyle = grad;
    ctx.fillRect(0, bottom ? h - band : 0, w, band);
  });

  // 格子（カード半枚ごと）
  const step = Math.max(20, scene.field.unit / 2);
  ctx.strokeStyle = rgba('#78aaff', v.matGridA);
  ctx.lineWidth = 2;
  ctx.beginPath();
  for (let x = step; x < w; x += step) { ctx.moveTo(x, 0); ctx.lineTo(x, h); }
  for (let y = step; y < h; y += step) { ctx.moveTo(0, y); ctx.lineTo(w, y); }
  ctx.stroke();

  // 中央線（2 人の境目）。**光は `shadowBlur` ではなく太い線の重ね**で出す ——
  // shadowBlur は毎フレーム描き直す紙では目に見えて重い（GPU の速い経路から外れる）
  ctx.beginPath();
  ctx.moveTo(0, h / 2);
  ctx.lineTo(w, h / 2);
  ctx.strokeStyle = rgba('#a0d2ff', 0.22);
  ctx.lineWidth = v.matCenterGlow;
  ctx.stroke();
  ctx.strokeStyle = rgba('#c8e6ff', 0.8);
  ctx.lineWidth = v.matCenterW;
  ctx.stroke();

  // 盤の外周
  ctx.strokeStyle = rgba('#ffffff', v.matEdgeA);
  ctx.lineWidth = 3;
  ctx.strokeRect(0, 0, w, h);
}

/**
 * 実写動画。**平面に置いて、平面で切る** —— 平面はマット 2 枚ぶんで、合成フィールド動画は上下に
 * 手札の帯が付いて縦に長いので、両端の帯は切り落ちてマットだけが入る。紙は平面より広いので、
 * 切らないと余白に帯がはみ出す。
 */
export function drawLiveField(ctx, scene) {
  const el = scene.video?.el;
  const size = scene.video?.size;
  if (!el || !(size?.w > 0) || !(size?.h > 0)) return;
  // まだ絵が無い（`HAVE_CURRENT_DATA` 未満）と drawImage が例外になる環境がある
  if (typeof el.readyState === 'number' && el.readyState < 2) return;
  const { w, h } = scene.field.plane;
  // 要素が元の映像の一部だけ（描く側は平面に入る部分だけ渡す。Issue #808）でも、置く場所は元の映像の座標で決まる
  const r = videoPlaneRect(scene.video, scene.field.plane);
  ctx.save();
  ctx.beginPath();
  ctx.rect(0, 0, w, h);
  ctx.clip();
  ctx.drawImage(el, r.x, r.y, r.w, r.h);
  ctx.restore();
}

/**
 * マットの枠線。**マットに刷られている区画**なので、枠はゾーンよりひと回り大きく、
 * 1 枚ぶんの場所には行き先の文字が入る。文字は**そのプレイヤーの向き**（奥の側は 180° 回す）。
 */
export function drawVirtualField(ctx, scene, cfg) {
  const v = fieldStyle(cfg);
  const { field } = scene;
  field.keys.forEach((key) => {
    const quad = field.zonePlane(key);
    const at = parseZoneKey(key);
    if (!quad || !at) return;
    const color = zoneColor(at.kind, v);
    const ink = decorOf(cfg?.style, 'field:matFront');
    inQuad(ctx, scaleQuad(quad, 1.12, 1.08), (w, h) => {
      const path = (c) => roundRectPath(c, 0, 0, w, h, Math.min(w, h) * 0.08);
      path(ctx);
      ctx.fillStyle = rgba(color, v.zoneFillA);
      ctx.fill();
      // 光る縁は**太い線の重ね**（`canvas/paint::glowStroke`。`shadowBlur` は毎フレームには重い）
      glowStroke(ctx, path, { ...ink, color, alpha: v.zoneEdgeA });
    });
    const label = ZONE_LABELS[at.kind];
    if (!label) return;
    inQuad(ctx, quad, (w, h) => {
      ctx.translate(w / 2, h / 2);
      if (field.side(at.player) === 'top') ctx.rotate(Math.PI);
      ctx.fillStyle = rgba(color, 0.75);
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      const size = Math.max(14, Math.min(w, h) * 0.22);
      ctx.font = `700 ${size}px sans-serif`;
      ctx.fillText(fitText(ctx, label, w * 0.9, size), 0, 0);
    });
  });
}

/** ゾーンの枠とキー（位置合わせの確認用。既定は濃さ 0 で、見たいときだけ上げる） */
export function drawZoneLines(ctx, scene, config = {}) {
  const v = fieldStyle(config);
  const { field } = scene;
  field.keys.forEach((key) => {
    const quad = field.zonePlane(key);
    const at = parseZoneKey(key);
    if (!quad || !at) return;
    quadPath(ctx, quad);
    ctx.strokeStyle = zoneColor(at.kind, v);
    ctx.lineWidth = 3;
    ctx.stroke();
    if (!config?.zoneLabels) return;
    ctx.fillStyle = 'rgba(255, 255, 255, 0.85)';
    ctx.font = '20px sans-serif';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
    ctx.fillText(key, quad[0][0] + 4, quad[0][1] + 22);
  });
}

/** 層の名前 → 描き方（`core/theme` の `FIELD_LAYERS` の名前だけ。欠けている層は描かれない） */
export default Object.freeze({
  matBack: drawMatBackground,
  video: drawLiveField,
  matFront: drawVirtualField,
  lines: drawZoneLines,
});
